"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    boxes: { label: string; desc: string }[];
    driversTitle: string;
    driversDescription: string;
    multipleAtOnce: string;
    factors: { market: string; size: string; value: string; momentum: string };
    factorTitle: string;
    factorDescription: string;
    todayReturn: string;
    capm: string;
    oneFactor: string;
    unexplained: string;
    factorModel: string;
    fourFactors: string;
    explainsMore: string;
    bottomNote: string;
  }
> = {
  ja: {
    boxes: [
      { label: "市場全体", desc: "多くの銘柄が一緒に動いた？" },
      { label: "グループ要因", desc: "似た銘柄群がまとめて動いた？" },
      { label: "個別材料", desc: "その会社だけの材料があった？" },
    ],
    driversTitle: "株価が動いた理由を3つに分ける",
    driversDescription: "理由を1つに決めつけず、どの箱が強かったかを見る",
    multipleAtOnce: "複数の箱が同時に効いていることも多い",
    factors: { market: "市場", size: "サイズ", value: "バリュー", momentum: "モメンタム" },
    factorTitle: "1要因から複数要因へ",
    factorDescription: "同じ+5.0%のリターンでも、要因を増やすと説明できる部分が広がる",
    todayReturn: "今日のリターン: +5.0%",
    capm: "資本資産価格モデル",
    oneFactor: "1要因",
    unexplained: "説明できない",
    factorModel: "ファクター",
    fourFactors: "4要因",
    explainsMore: "より多くのことを説明できる",
    bottomNote: "要因を増やすほど「なぜ動いたか」を整理しやすくなる",
  },
  en: {
    boxes: [
      { label: "The whole market", desc: "Did most shares move together?" },
      { label: "Group factors", desc: "Did a group of similar shares move together?" },
      { label: "Company-specific news", desc: "Was there news about this company alone?" },
    ],
    driversTitle: "Split the reason a share price moved into three",
    driversDescription: "Rather than settling on one reason, look at which box was strongest",
    multipleAtOnce: "Often more than one box is at work at the same time",
    factors: { market: "Market", size: "Size", value: "Value", momentum: "Momentum" },
    factorTitle: "From one factor to several",
    factorDescription: "For the same +5.0% return, adding factors widens the part you can explain",
    todayReturn: "Today's return: +5.0%",
    capm: "Capital Asset Pricing Model",
    oneFactor: "1 factor",
    unexplained: "Unexplained",
    factorModel: "Factor",
    fourFactors: "4 factors",
    explainsMore: "Explains more of the move",
    bottomNote: "The more factors you add, the easier it is to sort out why it moved",
  },
};

// ── 2-3 section 1: 株価変動を3つの箱に分解する ────────────────────

export function DocPriceMoveDriversDiagram() {
  const t = STRINGS[useDocLocale()];
  const boxColors = [COLOR.sky, COLOR.amber, COLOR.rose] as const;
  const boxes = t.boxes.map((b, i) => ({ ...b, color: boxColors[i]! }));

  const boxW = 280;
  const boxH = 36;
  const gap = 8;
  const startX = 50;
  const startY = 12;

  return (
    <IllustrationCard title={t.driversTitle} description={t.driversDescription}>
      <svg viewBox="0 0 360 160" className="mx-auto w-full max-w-md">
        {boxes.map((b, i) => {
          const y = startY + i * (boxH + gap);
          const cy = y + boxH / 2;

          return (
            <g key={b.label}>
              {/* Number */}
              <text
                x={startX - 16}
                y={cy + 1}
                textAnchor="middle"
                fontSize="14"
                fill={b.color}
                fontWeight="800"
                dominantBaseline="central"
                opacity={0.65}
              >
                {i + 1}
              </text>

              {/* Box */}
              <rect
                x={startX}
                y={y}
                width={boxW}
                height={boxH}
                rx={8}
                fill={b.color}
                opacity={0.18}
                stroke={b.color}
                strokeWidth="1.5"
                strokeOpacity={0.45}
              />

              {/* Label */}
              <text x={startX + 14} y={cy - 4} fontSize="11" fill={b.color} fontWeight="700">
                {b.label}
              </text>
              <text x={startX + 14} y={cy + 10} fontSize="8.5" fill="currentColor" opacity={0.7}>
                {b.desc}
              </text>
            </g>
          );
        })}

        {/* Bottom note */}
        <text
          x={startX + boxW / 2}
          y={startY + 3 * (boxH + gap) + 6}
          textAnchor="middle"
          fontSize="8"
          fill="currentColor"
          opacity={0.45}
        >
          {t.multipleAtOnce}
        </text>
      </svg>
    </IllustrationCard>
  );
}

// ── 2-3 section 2: CAPM → ファクターモデルの広がり ─────────────────

type FactorKey = "market" | "size" | "value" | "momentum";
type Segment = { key: FactorKey | "unknown"; value: number; color: string; isUnknown?: boolean };

const CAPM_SEGMENTS: Segment[] = [
  { key: "market", value: 2.0, color: COLOR.sky },
  { key: "unknown", value: 3.0, color: "currentColor", isUnknown: true },
];

const FF_SEGMENTS: Segment[] = [
  { key: "market", value: 2.0, color: COLOR.sky },
  { key: "size", value: 1.2, color: COLOR.amber },
  { key: "value", value: 0.8, color: COLOR.emerald },
  { key: "momentum", value: 0.5, color: COLOR.rose },
  { key: "unknown", value: 0.5, color: "currentColor", isUnknown: true },
];

export function DocFactorModelDiagram() {
  const t = STRINGS[useDocLocale()];
  const W = 440;
  const barLeft = 120;
  const barRight = W - 24;
  const barFullW = barRight - barLeft;
  const totalVal = 5.0;
  const pxPerVal = barFullW / totalVal;
  const barH = 40;

  const capmY = 50;
  const ffY = 150;
  const viewH = ffY + barH + 48;

  function renderBar(segments: Segment[], y: number) {
    let x = barLeft;
    return (
      <g>
        {segments.map((s) => {
          const w = s.value * pxPerVal;
          const cx = x + w / 2;
          const cy = y + barH / 2;
          const isFirst = x === barLeft;
          const isLast = x + w >= barLeft + totalVal * pxPerVal - 1;
          const rx = isFirst && isLast ? 10 : isFirst ? "10 0 0 10" : isLast ? "0 10 10 0" : 0;
          const el = (
            <g key={s.key + String(y)}>
              <rect
                x={x}
                y={y}
                width={w}
                height={barH}
                rx={typeof rx === "number" ? rx : undefined}
                fill={s.isUnknown ? "currentColor" : s.color}
                fillOpacity={s.isUnknown ? 0.05 : 0.15}
                stroke={s.isUnknown ? "currentColor" : s.color}
                strokeWidth="1"
                strokeOpacity={s.isUnknown ? 0.15 : 0.35}
                strokeDasharray={s.isUnknown ? "5 3" : "none"}
              />
              {w > 36 ? (
                <g>
                  <text
                    x={cx}
                    y={cy - 5}
                    textAnchor="middle"
                    fontSize={s.isUnknown ? "9" : "10.5"}
                    fill={s.isUnknown ? "currentColor" : s.color}
                    fontWeight="700"
                    fillOpacity={s.isUnknown ? 0.4 : 0.85}
                  >
                    {s.key === "unknown" ? "???" : t.factors[s.key]}
                  </text>
                  <text
                    x={cx}
                    y={cy + 10}
                    textAnchor="middle"
                    fontSize="9"
                    fill={s.isUnknown ? "currentColor" : s.color}
                    fillOpacity={s.isUnknown ? 0.3 : 0.6}
                  >
                    +{s.value.toFixed(1)}%
                  </text>
                </g>
              ) : (
                <text
                  x={cx}
                  y={cy + 1}
                  textAnchor="middle"
                  fontSize="8"
                  dominantBaseline="central"
                  fill={s.isUnknown ? "currentColor" : s.color}
                  fontWeight="600"
                  fillOpacity={s.isUnknown ? 0.35 : 0.7}
                >
                  {s.value.toFixed(1)}
                </text>
              )}
            </g>
          );
          x += w;
          return el;
        })}
      </g>
    );
  }

  const arrowX = barLeft + barFullW / 2;

  return (
    <IllustrationCard title={t.factorTitle} description={t.factorDescription}>
      <svg viewBox={`0 0 ${W} ${viewH}`} className="mx-auto w-full max-w-lg">
        {/* Total return badge */}
        <rect
          x={arrowX - 70}
          y={8}
          width={140}
          height={26}
          rx={13}
          fill="currentColor"
          fillOpacity={0.04}
          stroke="currentColor"
          strokeWidth="0.5"
          strokeOpacity={0.1}
        />
        <text
          x={arrowX}
          y={25}
          textAnchor="middle"
          fontSize="12"
          fill="currentColor"
          fontWeight="700"
          fillOpacity={0.7}
        >
          {t.todayReturn}
        </text>

        {/* ── CAPM row ── */}
        <text
          x={barLeft - 10}
          y={capmY + barH / 2 - 12}
          textAnchor="end"
          fontSize="8"
          fill="currentColor"
          fillOpacity={0.4}
        >
          {t.capm}
        </text>
        <text
          x={barLeft - 10}
          y={capmY + barH / 2 + 1}
          textAnchor="end"
          fontSize="11"
          fill="currentColor"
          fontWeight="700"
          fillOpacity={0.7}
        >
          CAPM
        </text>
        <text
          x={barLeft - 10}
          y={capmY + barH / 2 + 14}
          textAnchor="end"
          fontSize="8"
          fill="currentColor"
          fillOpacity={0.4}
        >
          {t.oneFactor}
        </text>
        {renderBar(CAPM_SEGMENTS, capmY)}

        {/* Unexplained annotation */}
        <g>
          <rect
            x={barLeft + 2.0 * pxPerVal + (3.0 * pxPerVal) / 2 - 50}
            y={capmY + barH + 6}
            width={100}
            height={18}
            rx={9}
            fill={COLOR.rose}
            fillOpacity={0.06}
          />
          <text
            x={barLeft + 2.0 * pxPerVal + (3.0 * pxPerVal) / 2}
            y={capmY + barH + 19}
            textAnchor="middle"
            fontSize="9"
            fill={COLOR.rose}
            fontWeight="600"
            fillOpacity={0.8}
          >
            {t.unexplained}
          </text>
        </g>

        {/* Arrow between rows */}
        <line
          x1={arrowX}
          y1={capmY + barH + 30}
          x2={arrowX}
          y2={ffY - 10}
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity={0.1}
        />
        <path
          d={`M ${arrowX - 4} ${ffY - 14} L ${arrowX} ${ffY - 8} L ${arrowX + 4} ${ffY - 14}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity={0.15}
        />

        {/* ── Factor Model row ── */}
        <text
          x={barLeft - 10}
          y={ffY + barH / 2 - 6}
          textAnchor="end"
          fontSize="10"
          fill="currentColor"
          fontWeight="700"
          fillOpacity={0.7}
        >
          {t.factorModel}
        </text>
        <text
          x={barLeft - 10}
          y={ffY + barH / 2 + 9}
          textAnchor="end"
          fontSize="8"
          fill="currentColor"
          fillOpacity={0.4}
        >
          {t.fourFactors}
        </text>
        {renderBar(FF_SEGMENTS, ffY)}

        {/* Explained annotation */}
        <g>
          <rect
            x={barLeft + (4.5 * pxPerVal) / 2 - 60}
            y={ffY + barH + 6}
            width={120}
            height={18}
            rx={9}
            fill={COLOR.emerald}
            fillOpacity={0.06}
          />
          <text
            x={barLeft + (4.5 * pxPerVal) / 2}
            y={ffY + barH + 19}
            textAnchor="middle"
            fontSize="9"
            fill={COLOR.emerald}
            fontWeight="600"
            fillOpacity={0.8}
          >
            {t.explainsMore}
          </text>
        </g>

        {/* Bottom note */}
        <text x={W / 2} y={viewH - 8} textAnchor="middle" fontSize="9" fill="currentColor" fillOpacity={0.4}>
          {t.bottomNote}
        </text>
      </svg>
    </IllustrationCard>
  );
}
